import React, { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";

import { SKY, outlineButtonClass } from "../marketUpdateTheme";

export const WELCOME_MAX = 750;

/** Ignite's "Edit your welcome message": one box, 750 characters. */
const EditWelcomeMessageModal = ({ value, onClose, onSave }) => {
  const [text, setText] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    boxRef.current?.focus();
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const over = text.length > WELCOME_MAX;

  const save = async () => {
    if (over || saving) return;
    setSaving(true);
    try {
      await onSave(text);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="welcome-title"
        className="w-full max-w-xl rounded-xl bg-white shadow-[0_24px_48px_-12px_rgba(17,24,39,0.35)]">
        <div className="relative px-5 py-4 border-b border-gray-100 text-center">
          <h2 id="welcome-title" className="text-base font-bold text-gray-900">Edit your welcome message</h2>
          <button type="button" onClick={onClose} aria-label="Close"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4">
          <label htmlFor="welcome-text" className="block text-sm font-semibold text-gray-800 mb-1.5">Your message</label>
          <textarea
            id="welcome-text"
            ref={boxRef}
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-describedby="welcome-count"
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm leading-relaxed text-gray-900 focus:outline-none focus:border-[#55c5d0] focus:ring-2 focus:ring-[#55c5d0]/15"
          />
          <p id="welcome-count" className={`text-xs mt-1 tabular-nums ${over ? "text-[#b42318] font-semibold" : "text-gray-500"}`}>
            {text.length}/{WELCOME_MAX} characters used{over ? `. Remove ${text.length - WELCOME_MAX} to save.` : ""}
          </p>
          <p className="text-xs text-gray-500 mt-2">"Dear [first name]," is added above it for each vendor.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={`${outlineButtonClass} px-4 py-2.5`}>Cancel</button>
          <button type="button" onClick={save} disabled={over || saving}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-40"
            style={{ backgroundColor: SKY }}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditWelcomeMessageModal;
